import {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  ChangeEvent,
} from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Divider,
} from '@mui/material';
import toast from 'react-hot-toast';

import { UploadType } from '@liscio/api';
import { hasVisibleHtmlContent, useApiClient } from '@liscio/common';
import {
  Box,
  Button,
  Icon,
  IconButton,
  IconType,
  RichTextEditor,
  Stack,
  TextField,
  Typography,
} from '@liscio/ui';

import {
  GUIDED_CUSTOM_STEP_LABEL,
  GUIDED_CUSTOM_STEP_TEXT_HTML,
  type GuidedCustomStepCommand,
  type GuidedCustomStepCommandSignal,
} from './customStepGuidedDemo';
import type {
  CustomFieldDefinition,
  FirmAuthoredFileRef,
} from 'modules/requests/hooks/useCreateCustomItem';

// ─── Types ──────────────────────────────────────────────────────────────────

type FieldTypeOption = {
  value: CustomFieldDefinition['fieldType'];
  label: string;
  icon: IconType;
};

const FIELD_TYPE_OPTIONS: FieldTypeOption[] = [
  { value: 'text', label: 'Text', icon: 'edit' },
  { value: 'file', label: 'File Upload', icon: 'upload' },
];

export type AddCustomItemDialogProps = {
  open: boolean;
  onClose: () => void;
  onSave: (fields: CustomFieldDefinition[]) => void;
  isSaving?: boolean;
  /** When `edit`, the dialog shows a single field and locks the step type (text vs file). */
  mode?: 'add' | 'edit';
  /** Seeded when `mode === 'add'` for guided/demo flows. */
  initialFieldsForAdd?: CustomFieldDefinition[];
  /** Seeded when `mode === 'edit'` (typically one field from the preview row). */
  initialFieldsForEdit?: CustomFieldDefinition[];
  guidedDemo?: boolean;
  sampleFileHref?: string;
  sampleFileName?: string;
  guidedDemoCommandSignal?: GuidedCustomStepCommandSignal | null;
  onGuidedDemoCommandComplete?: (
    command: GuidedCustomStepCommand,
    success?: boolean
  ) => void;
};

type UploadGuard = {
  fieldId: string;
  expectedFieldType: CustomFieldDefinition['fieldType'];
};

const ACCEPTED_CUSTOM_FILE_INPUT = '.pdf,image/png,image/jpeg,image/jpg,image/gif,image/webp';

const ACCEPTED_CUSTOM_FILE_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.gif', '.webp'];

const ACCEPTED_CUSTOM_FILE_MIME_TYPES = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
]);

const guidedSuggestionButtonSx = {
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: '-45%',
    width: '35%',
    background:
      'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.7), transparent)',
    animation: 'guided-demo-suggestion-wave 1.8s ease-in-out infinite',
  },
  '@keyframes guided-demo-suggestion-wave': {
    '0%': { left: '-45%' },
    '60%': { left: '110%' },
    '100%': { left: '110%' },
  },
};

function isAcceptedCustomFile(file: File): boolean {
  const mime = String(file.type || '').trim().toLowerCase();
  if (mime) return ACCEPTED_CUSTOM_FILE_MIME_TYPES.has(mime);
  const name = file.name.toLowerCase();
  return ACCEPTED_CUSTOM_FILE_EXTENSIONS.some((ext) => name.endsWith(ext));
}

async function loadGuidedSampleFile(
  sampleFileHref: string,
  sampleFileName: string
): Promise<File> {
  const response = await fetch(sampleFileHref);
  if (!response.ok) {
    throw new Error('Sample file request failed');
  }
  const blob = await response.blob();
  const file = new File([blob], sampleFileName, {
    type: blob.type || 'application/pdf',
  });
  if (!isAcceptedCustomFile(file)) {
    throw new Error('Sample file type is not supported');
  }
  return file;
}

/** Step title when the firm hasn’t typed a label yet — derived from the uploaded file name. */
function defaultLabelFromUploadedFileName(fileName: string): string {
  const base = fileName.trim() || 'Uploaded file';
  const withoutExt = base.replace(/\.[^.]+$/, '');
  return withoutExt.trim() || base;
}

/**
 * Removes one field id from the per-field upload-in-progress map.
 *
 * Do not short-circuit with `if (!prev[fieldId]) return prev`: React can apply this
 * functional update before the matching "set true" update commits, which would no-op
 * the clear and leave the UI stuck on "Uploading…" (see tests).
 */
export function clearFieldUploadFlag(
  prev: Record<string, boolean>,
  fieldId: string
): Record<string, boolean> {
  const { [fieldId]: removed, ...rest } = prev;
  void removed;
  return rest;
}

// ─── Field Row ──────────────────────────────────────────────────────────────

const FieldRow = ({
  field,
  index,
  onUpdate,
  onDelete,
  canDelete,
  onUploadFile,
  isUploading,
  lockFieldType = false,
  guidedDemo = false,
  sampleFileHref,
  sampleFileName,
}: {
  field: CustomFieldDefinition;
  index: number;
  onUpdate: (index: number, updates: Partial<CustomFieldDefinition>) => void;
  onDelete: (index: number) => void;
  canDelete: boolean;
  onUploadFile: (index: number, file: File) => Promise<void>;
  isUploading: boolean;
  lockFieldType?: boolean;
  guidedDemo?: boolean;
  sampleFileHref?: string;
  sampleFileName?: string;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingSampleFile, setIsUploadingSampleFile] = useState(false);

  const uploadDisabled = isUploading || isUploadingSampleFile;

  /** TinyMCE `onEditorChange` — first argument is HTML string. */
  const handleRichTextEditorChange = (html: string) => {
    onUpdate(index, { textContent: html });
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!isAcceptedCustomFile(file)) {
      toast.error(
        'Please upload a PDF or image file (PNG, JPG, JPEG, GIF, or WEBP).'
      );
      return;
    }
    await onUploadFile(index, file);
  };

  const handleUseSampleFile = async () => {
    if (!sampleFileHref || !sampleFileName) return;

    setIsUploadingSampleFile(true);
    try {
      const file = await loadGuidedSampleFile(sampleFileHref, sampleFileName);
      await onUploadFile(index, file);
    } catch {
      toast.error('Could not attach the sample PDF. Please upload a file instead.');
    } finally {
      setIsUploadingSampleFile(false);
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 1,
        bgcolor: 'grey.50',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack spacing={2}>
        {canDelete && (
          <Stack direction="row" justifyContent="flex-end" alignItems="center">
            <IconButton size="small" onClick={() => onDelete(index)}>
              <Icon icon="close" />
            </IconButton>
          </Stack>
        )}

        <Box data-tour-id="tax-delivery-custom-step-label">
          <TextField
            label="Label"
            placeholder="e.g., Review Filing Instructions"
            value={field.label}
            onChange={(e) => onUpdate(index, { label: e.target.value })}
            size="small"
            fullWidth
            required
          />
          {guidedDemo && (
            <Button
              variant="text"
              size="small"
              onClick={() => onUpdate(index, { label: GUIDED_CUSTOM_STEP_LABEL })}
              sx={{ mt: 0.5, ...guidedSuggestionButtonSx }}
            >
              Use suggested label
            </Button>
          )}
        </Box>

        <Stack direction="row" spacing={2} alignItems="center">
          <Box data-tour-id="tax-delivery-custom-step-type">
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id={`field-type-label-${index}`}>Type</InputLabel>
              <Select
                labelId={`field-type-label-${index}`}
                value={field.fieldType}
                label="Type"
                disabled={lockFieldType}
                onChange={(e: SelectChangeEvent) =>
                  onUpdate(index, {
                    fieldType: e.target
                      .value as CustomFieldDefinition['fieldType'],
                    textContent: '',
                    firmFiles: [],
                  })
                }
              >
                {FIELD_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Icon icon={opt.icon} sx={{ fontSize: 18 }} />
                      <span>{opt.label}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Stack>

        {field.fieldType === 'text' && (
          <Stack spacing={0.75}>
            <Typography variant="subtitle2" color="text.primary">
              Text content*
            </Typography>
            <Box data-tour-id="tax-delivery-custom-step-text">
              <RichTextEditor
                value={field.textContent || ''}
                onChange={handleRichTextEditorChange}
                height="220px"
                inlineImages={false}
                defaultTextAlign="center"
              />
            </Box>
          </Stack>
        )}

        {field.fieldType === 'file' && (
          <Stack spacing={2}>
            <Stack spacing={0.75}>
              <Typography variant="subtitle2" color="text.primary">
                Text content
              </Typography>
              <Box data-tour-id="tax-delivery-custom-step-text">
                <RichTextEditor
                  value={field.textContent || ''}
                  onChange={handleRichTextEditorChange}
                  height="180px"
                  inlineImages={false}
                  defaultTextAlign="center"
                  defaultBold
                />
                {guidedDemo && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() =>
                      onUpdate(index, { textContent: GUIDED_CUSTOM_STEP_TEXT_HTML })
                    }
                    sx={{ mt: 0.5, ...guidedSuggestionButtonSx }}
                  >
                    Use suggested instructions
                  </Button>
                )}
              </Box>
            </Stack>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_CUSTOM_FILE_INPUT}
              hidden
              onChange={handleFileChange}
            />
            <Box
              data-tour-id="tax-delivery-custom-step-upload"
              sx={{ alignSelf: 'flex-start' }}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<Icon icon="upload" />}
                disabled={uploadDisabled}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploadDisabled
                  ? 'Uploading…'
                  : (field.firmFiles || []).length > 0
                    ? 'Replace file'
                    : 'Upload file'}
              </Button>
              {guidedDemo && sampleFileHref && sampleFileName && (
                <Button
                  variant="contained"
                  size="small"
                  disabled={uploadDisabled}
                  onClick={handleUseSampleFile}
                  sx={{ ml: 1, ...guidedSuggestionButtonSx }}
                >
                  {isUploadingSampleFile ? 'Attaching…' : 'Use sample PDF'}
                </Button>
              )}
            </Box>
            {(field.firmFiles || []).length > 0 && (
              <Stack spacing={0.5}>
                {(field.firmFiles || []).map((f) => (
                  <Stack
                    key={f.uuid}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="body2">{f.name}</Typography>
                    <IconButton
                      size="small"
                      aria-label="remove file"
                      onClick={() =>
                        onUpdate(index, {
                          firmFiles: (field.firmFiles || []).filter(
                            (x) => x.uuid !== f.uuid
                          ),
                        })
                      }
                    >
                      <Icon icon="close" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

// ─── Main Dialog ────────────────────────────────────────────────────────────

const createEmptyField = (): CustomFieldDefinition => ({
  id: `field_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  label: '',
  description: '',
  fieldType: 'text',
  required: false,
  textContent: '',
  firmFiles: [],
});

export const AddCustomItemDialog = ({
  open,
  onClose,
  onSave,
  isSaving = false,
  mode = 'add',
  initialFieldsForAdd,
  initialFieldsForEdit,
  guidedDemo = false,
  sampleFileHref,
  sampleFileName,
  guidedDemoCommandSignal,
  onGuidedDemoCommandComplete,
}: AddCustomItemDialogProps) => {
  const apiClient = useApiClient();
  const [fields, setFields] = useState<CustomFieldDefinition[]>([
    createEmptyField(),
  ]);
  const fieldsRef = useRef(fields);
  const isMountedRef = useRef(true);
  const isOpenRef = useRef(open);
  const handledGuidedCommandSignalRef = useRef<number | null>(null);
  useEffect(() => {
    fieldsRef.current = fields;
  }, [fields]);
  useEffect(() => {
    isOpenRef.current = open;
  }, [open]);
  // Strict Mode runs effect cleanup then re-runs the effect. We must set `true` on each
  // subscribe; otherwise cleanup leaves `isMountedRef` false and `finally` skips clearing
  // `uploadingFieldIds` → stuck "Uploading…" after a successful upload.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const [uploadingFieldIds, setUploadingFieldIds] = useState<Record<string, boolean>>({});

  const handleEnter = useCallback(() => {
    if (mode === 'edit' && initialFieldsForEdit?.length) {
      setFields(
        initialFieldsForEdit.map((f) => ({
          ...f,
          id: f.id || `field_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        }))
      );
    } else if (mode === 'add' && initialFieldsForAdd?.length) {
      setFields(
        initialFieldsForAdd.map((f) => ({
          ...f,
          id: f.id || `field_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          firmFiles: f.firmFiles ? [...f.firmFiles] : [],
        }))
      );
    } else {
      setFields([createEmptyField()]);
    }
    setUploadingFieldIds({});
  }, [mode, initialFieldsForAdd, initialFieldsForEdit]);

  const handleUpdateField = useCallback(
    (index: number, updates: Partial<CustomFieldDefinition>) => {
      setFields((prev) =>
        prev.map((f, i) => (i === index ? { ...f, ...updates } : f))
      );
    },
    []
  );

  const handleDeleteField = useCallback((index: number) => {
    setFields((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleAddField = useCallback(() => {
    setFields((prev) => [...prev, createEmptyField()]);
  }, []);

  const handleUploadFile = useCallback(
    async (index: number, file: File) => {
      const field = fieldsRef.current[index];
      if (!field) return;
      const uploadGuard: UploadGuard = {
        fieldId: field.id,
        expectedFieldType: field.fieldType,
      };
      setUploadingFieldIds((prev) => ({
        ...prev,
        [uploadGuard.fieldId]: true,
      }));
      try {
        const res = await apiClient.documents.uploadFile({
          file,
          type: UploadType.NewFile,
        });
        const doc = res.data.data;
        const ref: FirmAuthoredFileRef = {
          id: doc.id,
          uuid: doc.uuid,
          name: doc.doc_name || file.name,
          contentType: doc.content_type || file.type,
        };
        if (!isMountedRef.current || !isOpenRef.current) {
          return;
        }
        setFields((prev) => {
          const targetIndex = prev.findIndex((f) => f.id === uploadGuard.fieldId);
          if (targetIndex < 0) {
            return prev;
          }
          const target = prev[targetIndex];
          if (
            target.fieldType !== 'file' ||
            target.fieldType !== uploadGuard.expectedFieldType
          ) {
            return prev;
          }
          return prev.map((f, i) =>
            i === targetIndex
              ? {
                  ...f,
                  // Single-file UX: upload replaces any prior attachment.
                  firmFiles: [ref],
                  label: f.label.trim()
                    ? f.label
                    : defaultLabelFromUploadedFileName(ref.name),
                }
              : f
          );
        });
      } catch {
        toast.error('Upload failed. Please try again.');
      } finally {
        // Always clear uploading state; do not gate on `isMountedRef` (can stay false in dev
        // after Strict Mode effect cycles). React 18 safely no-ops if unmounted.
        setUploadingFieldIds((prev) =>
          clearFieldUploadFlag(prev, uploadGuard.fieldId)
        );
      }
    },
    [apiClient.documents]
  );

  const handleAttachSampleFile = useCallback(async () => {
    if (!sampleFileHref || !sampleFileName) return false;

    try {
      const file = await loadGuidedSampleFile(sampleFileHref, sampleFileName);
      await handleUploadFile(0, file);
      return true;
    } catch {
      toast.error('Could not attach the sample PDF. Please upload a file instead.');
      return false;
    }
  }, [handleUploadFile, sampleFileHref, sampleFileName]);

  const isValid = useMemo(() => {
    if (fields.length === 0) return false;
    return fields.every((f) => {
      if (!f.label.trim()) return false;
      if (f.fieldType === 'text') {
        return hasVisibleHtmlContent(f.textContent);
      }
      if (f.fieldType === 'file') {
        return (f.firmFiles || []).length > 0;
      }
      return false;
    });
  }, [fields]);

  const handleSave = useCallback(() => {
    if (!isValid) return;
    // Product intent: custom steps in this modal are review+approve only.
    // Keep compatibility for old rows but do not persist/create new "[REQUIRED]" custom prompts.
    onSave(fields.map((f) => ({ ...f, required: false })));
  }, [fields, isValid, onSave]);

  useEffect(() => {
    if (!guidedDemo || !guidedDemoCommandSignal || !open) {
      return;
    }

    if (handledGuidedCommandSignalRef.current === guidedDemoCommandSignal.signalId) {
      return;
    }
    handledGuidedCommandSignalRef.current = guidedDemoCommandSignal.signalId;

    const complete = (success = true) =>
      onGuidedDemoCommandComplete?.(guidedDemoCommandSignal.command, success);

    if (guidedDemoCommandSignal.command === 'fillLabel') {
      handleUpdateField(0, { label: GUIDED_CUSTOM_STEP_LABEL });
      complete();
      return;
    }

    if (guidedDemoCommandSignal.command === 'fillInstructions') {
      handleUpdateField(0, { textContent: GUIDED_CUSTOM_STEP_TEXT_HTML });
      complete();
      return;
    }

    if (guidedDemoCommandSignal.command === 'attachSamplePdf') {
      handleAttachSampleFile().then((attached) => {
        complete(attached);
      });
      return;
    }

    if (guidedDemoCommandSignal.command === 'save') {
      if (!isValid) {
        complete(false);
        return;
      }
      handleSave();
    }
  }, [
    guidedDemo,
    guidedDemoCommandSignal,
    handleAttachSampleFile,
    handleSave,
    handleUpdateField,
    isValid,
    onGuidedDemoCommandComplete,
    open,
  ]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      // TinyMCE opens link/image dialogs in a portal outside this node; MUI’s default focus
      // trap would otherwise block focusing URL/title inputs while dropdowns still work.
      disableEnforceFocus
      TransitionProps={{ onEnter: handleEnter }}
    >
      <DialogTitle>
        <Stack spacing={0.5}>
          <Typography variant="h6">
            {mode === 'edit' ? 'Edit custom step' : 'Add Custom Item'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {mode === 'edit'
              ? 'Update the label and content shown to your client. Step type (text vs file) cannot be changed.'
              : 'Add filing instructions, invoices, or other items for clients to review and approve as part of their tax return delivery.'}
          </Typography>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }} data-tour-id="tax-delivery-custom-step-dialog">
        <Stack spacing={2}>
          {fields.map((field, index) => (
            <FieldRow
              key={field.id}
              field={field}
              index={index}
              onUpdate={handleUpdateField}
              onDelete={handleDeleteField}
              canDelete={mode !== 'edit' && fields.length > 1}
              onUploadFile={handleUploadFile}
              isUploading={Boolean(uploadingFieldIds[field.id])}
              lockFieldType={mode === 'edit'}
              guidedDemo={guidedDemo}
              sampleFileHref={sampleFileHref}
              sampleFileName={sampleFileName}
            />
          ))}

          {mode !== 'edit' && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleAddField}
              sx={{ alignSelf: 'flex-start' }}
              startIcon={<Icon icon="add" />}
            >
              Add Another Field
            </Button>
          )}
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Box data-tour-id="tax-delivery-custom-step-save">
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!isValid || isSaving}
          >
            {isSaving ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Save'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
